/** Pflanze anlegen oder bearbeiten – inkl. eigener Aufgaben und Abstände. */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import {
  allSpecies,
  allTaskTypes,
  findSpecies,
  type CareTask,
  type Plant,
  type PotMaterial,
  type WaterType,
} from '@pflanzen/shared';
import { mutate, useStore } from '../lib/store';
import { path } from '../lib/api';
import { Button, Card, SectionTitle, Toggle } from '../ui/kit';

const POTS: { key: PotMaterial; label: string }[] = [
  { key: 'plastic', label: 'Plastik' },
  { key: 'terracotta', label: 'Ton (Terrakotta)' },
  { key: 'ceramic', label: 'Keramik glasiert' },
  { key: 'self_watering', label: 'Selbstbewässerung' },
  { key: 'other', label: 'Anderes' },
];
const WATERS: { key: WaterType; label: string }[] = [
  { key: 'tap', label: 'Leitungswasser' },
  { key: 'filtered', label: 'Gefiltert' },
  { key: 'rain', label: 'Regenwasser' },
  { key: 'distilled', label: 'Destilliert' },
  { key: 'mixed', label: 'Gemischt' },
];

const field = 'mt-1 block min-h-12 w-full rounded-2xl border border-line bg-paper px-3.5 text-[16px]';

export function PlantEditPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { state } = useStore();
  const existing = state?.plants.find((p) => p.id === id) ?? null;
  const [form, setForm] = useState(() => ({
    name: existing?.name ?? '',
    speciesKey: existing?.speciesKey ?? '',
    roomId: existing?.roomId ?? state?.rooms[0]?.id ?? '',
    emoji: existing?.emoji ?? '🪴',
    potMaterial: existing?.potMaterial ?? ('plastic' as PotMaterial),
    potDiameterCm: existing?.potDiameterCm ?? null,
    waterType: existing?.waterType ?? ('tap' as WaterType),
    hasSaucer: existing?.hasSaucer ?? true,
    notes: existing?.notes ?? '',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!state) return null;

  const species = allSpecies(state.species);
  const types = allTaskTypes(state.taskTypes);
  const tasks = existing ? state.tasks.filter((t) => t.plantId === existing.id) : [];
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  const pickSpecies = (key: string) => {
    const sp = findSpecies(key, state.species);
    setForm((f) => ({ ...f, speciesKey: key, emoji: sp?.emoji ?? f.emoji, name: f.name || (sp?.name ?? '') }));
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return setError('Bitte einen Namen eingeben.');
    setSaving(true);
    setError(null);
    const body = { ...form, name: form.name.trim(), speciesKey: form.speciesKey || null };
    try {
      if (existing) {
        await mutate('PATCH', path('plant', { id: existing.id }), body);
        nav(`/pflanzen/${existing.id}`);
      } else {
        const created = await mutate<Plant>('POST', path('plants'), { ...body, createDefaultTasks: true });
        nav(`/pflanzen/${created.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const updateTask = (task: CareTask, patch: Partial<CareTask>) => void mutate('PATCH', path('task', { id: task.id }), patch);
  const addTask = (typeKey: string) => {
    if (!existing || !typeKey) return;
    void mutate('POST', path('tasks'), { plantId: existing.id, typeKey, intervalDays: null, enabled: true, pausedUntil: null });
  };
  const archive = async () => {
    if (!existing || !confirm(`${existing.name} archivieren? Verlauf und Fotos bleiben erhalten.`)) return;
    await mutate('DELETE', path('plant', { id: existing.id }));
    nav('/pflanzen');
  };

  return (
    <form onSubmit={save} className="px-4 pb-8">
      <header className="pt-safe flex items-center justify-between pb-2">
        <Link to={existing ? `/pflanzen/${existing.id}` : '/pflanzen'} className="-ml-2 flex items-center gap-0.5 px-2 py-2 text-[15px] font-semibold text-moss">
          <ChevronLeft className="h-5 w-5" /> Zurück
        </Link>
      </header>
      <h1 className="font-display text-[30px] leading-tight">{existing ? 'Bearbeiten' : 'Neue Pflanze'}</h1>

      <Card className="mt-4 space-y-3">
        <label className="block text-sm text-muted">
          Art (Steckbrief)
          <select className={field} value={form.speciesKey} onChange={(e) => pickSpecies(e.target.value)}>
            <option value="">– eigene / unbekannt –</option>
            {species.map((s) => (
              <option key={s.key} value={s.key}>
                {s.emoji} {s.name}
              </option>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-[64px_1fr] gap-2">
          <label className="block text-sm text-muted">
            Symbol
            <input className={`${field} text-center text-xl`} value={form.emoji} maxLength={4} onChange={(e) => set('emoji', e.target.value)} />
          </label>
          <label className="block text-sm text-muted">
            Name
            <input className={field} value={form.name} placeholder="z. B. Calathea" onChange={(e) => set('name', e.target.value)} />
          </label>
        </div>
        <label className="block text-sm text-muted">
          Raum
          <select className={field} value={form.roomId} onChange={(e) => set('roomId', e.target.value)}>
            {state.rooms.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-sm text-muted">
            Topf
            <select className={field} value={form.potMaterial} onChange={(e) => set('potMaterial', e.target.value as PotMaterial)}>
              {POTS.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm text-muted">
            Topf-Ø (cm)
            <input
              className={field}
              inputMode="numeric"
              value={form.potDiameterCm ?? ''}
              placeholder="–"
              onChange={(e) => set('potDiameterCm', e.target.value ? Number(e.target.value) : null)}
            />
          </label>
        </div>
        <label className="block text-sm text-muted">
          Wasser
          <select className={field} value={form.waterType} onChange={(e) => set('waterType', e.target.value as WaterType)}>
            {WATERS.map((w) => (
              <option key={w.key} value={w.key}>
                {w.label}
              </option>
            ))}
          </select>
        </label>
        <Toggle label="Steht in einem Untersetzer" checked={form.hasSaucer} onChange={(v) => set('hasSaucer', v)} />
        <label className="block text-sm text-muted">
          Notizen
          <textarea className={`${field} min-h-24 py-2.5`} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
        </label>
      </Card>
      {error && <p className="mt-3 text-sm text-alert">{error}</p>}
      <Button type="submit" disabled={saving} className="mt-4 w-full">
        {saving ? 'Speichere …' : 'Speichern'}
      </Button>

      {existing && (
        <>
          <SectionTitle>Aufgaben</SectionTitle>
          <p className="-mt-1 mb-2 px-1 text-xs text-muted">Leer lassen = Wert aus dem Steckbrief (die App lernt dazu).</p>
          <Card className="divide-y divide-line py-1">
            {tasks.map((t) => {
              const type = types.find((x) => x.key === t.typeKey);
              return (
                <div key={t.id} className="flex items-center gap-3 py-2.5">
                  <span className="text-lg">{type?.icon ?? '•'}</span>
                  <span className="flex-1 text-[15px]">{type?.label ?? t.typeKey}</span>
                  <input
                    className="h-10 w-16 rounded-xl border border-line bg-paper text-center text-[15px]"
                    inputMode="decimal"
                    defaultValue={t.intervalDays ?? ''}
                    placeholder="auto"
                    aria-label="Abstand in Tagen"
                    onBlur={(e) => updateTask(t, { intervalDays: e.target.value ? Number(e.target.value.replace(',', '.')) : null })}
                  />
                  <span className="text-xs text-muted">Tage</span>
                  <input
                    type="checkbox"
                    role="switch"
                    {...{ switch: '' }}
                    checked={t.enabled}
                    aria-label="Aktiv"
                    onChange={(e) => updateTask(t, { enabled: e.target.checked })}
                    className="toggle shrink-0"
                  />
                </div>
              );
            })}
          </Card>
          <select className={`${field} mt-2.5`} value="" onChange={(e) => addTask(e.target.value)}>
            <option value="">+ Aufgabe hinzufügen …</option>
            {types
              .filter((ty) => !tasks.some((t) => t.typeKey === ty.key))
              .map((ty) => (
                <option key={ty.key} value={ty.key}>
                  {ty.icon} {ty.label}
                </option>
              ))}
          </select>
          <Button tone="ghost" onClick={() => void archive()} className="mt-8 w-full text-alert">
            Pflanze archivieren
          </Button>
        </>
      )}
    </form>
  );
}
